// AboutSection.tsx
import React from "react";
import "./AboutSection.css";
import abtImage1 from "../../Assets/about_section_image-1.jpg";
import abtImage2 from "../../Assets/about_section_image-2.jpg";

interface Stat {
  title: string;
  desc: string;
  color: string;
}

export const AboutSection: React.FC = () => {
  const stats: Stat[] = [
    {
      title: "Nationwide Shipping",
      desc: "Fast & reliable delivery",
      color: "pink-gradient",
    },
    {
      title: "Premium Quality",
      desc: "Best materials guaranteed",
      color: "green-gradient",
    },
    {
      title: "24/7 Support",
      desc: "Always here for you",
      color: "blue-gradient",
    },
    {
      title: "Easy Returns",
      desc: "Hassle-free return policy",
      color: "purple-gradient",
    },
  ];

  return (
    <div className="about-container">
      {/* Hero Section */}
      <section className="hero-section">
        {/* Decorative Elements */}
        <div className="hero-decoration hero-decoration-1"></div>
        <div className="hero-decoration hero-decoration-2"></div>

        <div className="hero-content">
          <h1 className="hero-title">About Us</h1>
          <div className="hero-underline"></div>
          <p className="hero-subtitle">
            Flooron.in's company and culture are built on{" "}
            <span className="highlight-pink">quality</span>,{" "}
            <span className="highlight-blue">trust</span>, and{" "}
            <span className="highlight-green">innovation</span> 
          </p>
        </div>
      </section>

      {/* Artistic Image Section */}
      <section className="image-section">
        <div className="image-card">
          <div className="image-card-content">
            <div className="image-main-wrapper">
              {/* Decorative circles */}
              <div className="floating-circle floating-circle-1"></div>
              <div className="floating-circle floating-circle-2"></div>

              <div className="image-main-box">
                <img
                  src={abtImage2}
                  alt="About Flooring"
                  className="main-image"
                />
              </div>
            </div>
          </div>

          {/* Scattered decorative shapes */}
          <div className="scattered-shape shape-1"></div>
          <div className="scattered-shape shape-2"></div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="mission-decoration mission-decoration-1"></div>
        <div className="mission-decoration mission-decoration-2"></div>

        <div className="mission-content">
          <div className="mission-grid">
            <div className="mission-image-wrapper">
              <div className="mission-image-card">
                <img
                  src={abtImage1}
                  alt="About Flooring"
                  className="main-image"
                  height={'100%'}
                />
              </div>
              {/* Floating decorations */}
              <div className="mission-float mission-float-1"></div>
              <div className="mission-float mission-float-2"></div>
            </div>

            <div className="mission-text-card">
              <div className="mission-badge">
                <span className="mission-badge-text">OUR MISSION</span>
              </div>
              <h2 className="mission-title">
                Transforming Crafting Solutions{" "}
                <span className="mission-title-highlight">Across India</span>
              </h2>
              <p className="mission-description">
                We believe in bringing premium crafting supplies to every creator
                across India. With a focus on quality, creativity, and customer
                experience, we're committed to transforming how hobbyists and
                businesses discover the perfect materials for their craft.
             </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        {/* Decorative dots pattern */}
        <div className="story-dots-pattern"></div>

        <div className="story-content">
          <div className="story-grid">
            <div className="story-text-card">
              <div className="story-badge">
                <span className="story-badge-text">EST. 2024</span>
              </div>
              <h2 className="story-title">
                Our <span className="story-title-highlight">Story</span>
              </h2>
              <p className="story-description">
                 Established in 2024, Flooron.in was founded with a vision to
                 revolutionize the crafting experience in India. We recognized the
                 need for a reliable, customer-centric platform that brings
                 premium crafting supplies and materials directly to creators,
                 hobbyists, and businesses. From tools to textures, Flooron is
                 your one-stop shop for all things crafting.
              </p>
              <p className="story-description">
                Our values define us —{" "}
                <span className="story-value-pink">quality</span>,{" "}
                <span className="story-value-blue">trust</span>, and{" "}
                <span className="story-value-green">innovation</span>. We are
                dedicated to providing premium materials with exceptional
                service.
              </p>
            </div>

            <div className="story-image-wrapper">
              <div className="story-image-card">
                <div className="story-logo">
                  <div className="story-logo-text">FLOORON</div>
                  <div className="story-logo-domain">.in</div>
                </div>
              </div>
              {/* Floating decorations */}
              <div className="story-float story-float-1"></div>
              <div className="story-float story-float-2"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        {/* Large decorative circles */}
        <div className="stats-decoration stats-decoration-1"></div>
        <div className="stats-decoration stats-decoration-2"></div>

        <div className="stats-content">
          <div className="stats-header">
            <div className="stats-badge">
              <span className="stats-badge-text">WHY CHOOSE US?</span>
            </div>
            <h2 className="stats-title">Why Choose Flooron.in</h2>
            <div className="stats-underline"></div>
          </div>

          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="stat-card-wrapper"
                style={{
                  animationDelay: `${0.15 * (index + 1)}s`,
                }}
              >
                <div className="stat-card">
                  {/* Gradient background on hover */}
                  <div className={`stat-card-hover-bg ${stat.color}`}></div>

                  <div className="stat-card-content">
                    <div className={`stat-icon-wrapper ${stat.color}`}>
                      {index === 0 && (
                        <svg
                          className="stat-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                          />
                        </svg>
                      )}
                      {index === 1 && (
                        <svg
                          className="stat-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                          />
                        </svg>
                      )}
                      {index === 2 && (
                        <svg
                          className="stat-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                      )}
                      {index === 3 && (
                        <svg
                          className="stat-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      )}
                    </div>
                    <h3 className="stat-title">{stat.title}</h3>
                    <p className="stat-description">{stat.desc}</p>
                  </div>

                  {/* Decorative corner element */}
                  <div className={`stat-corner-decoration ${stat.color}`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
